import styled from "styled-components";
import { useAppState } from "../state/store";
import {
  laskeEfektiivinenPaasy,
  tunnistaDemoProfiili,
  TOIMINTO_NIMET,
  annaProfiiliTrivoreKayttajalle,
} from "../utils/oikeudet";
import { useGetCurrentUserQuery } from "../api/backendApi";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { useTranslation } from "react-i18next";

/**
 * Demo-protohuomiolaatikko etusivulla. K5 S4 (10.6.2026): listaa selkeästi
 * mitkä toiminnot/näkymät ovat KÄYTÖSSÄ ja mitkä PIILOSSA tällä profiililla.
 *
 * Sama `laskeEfektiivinenPaasy`-helper kuin myöntönäkymän "efektiivinen
 * pääsy" -yhteenveto → yksi totuuslähde (oikeudet.ts).
 */

const Container = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
`;

const ProtoRaita = styled.div`
  background: ${({ theme }) => theme.colors.warningBase};
  color: ${({ theme }) => theme.colors.blackBase};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.6875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.m};
`;

const Sisalto = styled.div`
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const Otsikkorivi = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.s};
  flex-wrap: wrap;
`;

const Rooli = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.brandBase};
`;

const Huomio = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
`;

const Sarakkeet = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.s};
`;

const Sarake = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.insetXxs};
`;

const SarakeOtsikko = styled.div<{ $tyyppi: "kaytossa" | "piilossa" }>`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme, $tyyppi }) =>
    $tyyppi === "kaytossa"
      ? theme.colors.successDark1
      : theme.colors.depthDark1};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const ToimintoRivi = styled.div<{ $tyyppi: "kaytossa" | "piilossa" }>`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme, $tyyppi }) =>
    $tyyppi === "kaytossa" ? theme.colors.blackBase : theme.colors.depthDark1};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xxs};

  &::before {
    content: "${({ $tyyppi }) => ($tyyppi === "kaytossa" ? "✓" : "✗")}";
    flex-shrink: 0;
    color: ${({ theme, $tyyppi }) =>
      $tyyppi === "kaytossa"
        ? theme.colors.successDark1
        : theme.colors.depthDark1};
  }
`;

export default function OikeusBanneri() {
  const { state } = useAppState();
  const { t, i18n } = useTranslation(["oikeusbanneri", "common"]);
  const { kayttaja, todellinenRooli, isLoading, error } = useTodellinenRooli();

  const { kaytossa, piilossa } = laskeEfektiivinenPaasy(
    todellinenRooli?.profiili,
  );
  return (
    <Container role="status" aria-live="polite">
      <ProtoRaita>{t(`oikeudet.huomio`)}</ProtoRaita>
      <Sisalto>
        <Otsikkorivi>
          <Rooli>
            {t(`roolit.rooli`, {
              ns: "common",
            })}
            :{" "}
            {t(`roolit.${todellinenRooli?.id ?? "mukautettu"}`, {
              ns: "common",
            })}
          </Rooli>
        </Otsikkorivi>
        <Sarakkeet>
          <Sarake>
            <SarakeOtsikko $tyyppi="kaytossa">
              Käytössä ({kaytossa.length})
            </SarakeOtsikko>
            {kaytossa.map((toiminto) => (
              <ToimintoRivi key={toiminto} $tyyppi="kaytossa">
                {t(`toiminnot.${toiminto}`, { ns: "common" })}
              </ToimintoRivi>
            ))}
          </Sarake>
          <Sarake>
            <SarakeOtsikko $tyyppi="piilossa">
              Piilossa ({piilossa.length})
            </SarakeOtsikko>
            {piilossa.map((toiminto) => (
              <ToimintoRivi key={toiminto} $tyyppi="piilossa">
                {t(`toiminnot.${toiminto}`, { ns: "common" })}
              </ToimintoRivi>
            ))}
          </Sarake>
        </Sarakkeet>
        <Huomio>{t(`teksti`, { ns: "oikeusbanneri" })}</Huomio>
      </Sisalto>
    </Container>
  );
}
